import React from "react";
import { useState, useEffect } from "react";
import "../styles/Match.css";
import { submitPrediction } from "../api/api.js";
import { useAuth } from "../context/AuthContext";
import AuthModal from "./AuthModal.jsx";
import useWindowWidth from "../helpers/useWindowWidth.jsx";

export default function Match({ match }) {
    const [matchStatus, setMatchStatus] = useState(determineMatchStatus(match.start_time));
    const [predictions, setPredictions] = useState(match.predictions);
    const { user, logout } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const width = useWindowWidth();
    const screen = width < 660 ? "small" : "big";

    const matchStarted = matchStatus === 'started';

    function determineMatchStatus(startTime) {
        const now = Math.floor(Date.now() / 1000);
        const startOfToday = new Date().setHours(0, 0, 0, 0) / 1000;
        const endOfToday = startOfToday + 24 * 60 * 60;

        if (now >= startTime) {
            return "started";
        } else if (now + 3600 >= startTime) {
            return "starting-in-one-hour";
        } else if (startTime >= startOfToday && startTime < endOfToday) {
            return "starting-today";
        } else {
            return "starting-tomorrow";
        }
    }
    
    useEffect(() => {
        const checkMatchStatus = () => {
            const newStatus = determineMatchStatus(match.start_time);
            if (newStatus !== matchStatus) {
                setMatchStatus(newStatus);
            }
        };
    
        // Check every second for better synchronization with the "minute 00"
        const interval = setInterval(() => {
            const currentSeconds = new Date().getSeconds();
            if (currentSeconds === 0 && matchStatus !== "started") {
                checkMatchStatus();
            }
        }, 1000);
    
        return () => clearInterval(interval); // Cleanup on component unmount
    }, [match.start_time, matchStatus]);

    const renderGoals = (matchStarted, team) => {
        const selected = predictions[team] ?? undefined;

        return [0, 1, 2, 3, 4, "+"].map((goal) => {
            const isPlus = goal === "+";

            return (
                <div
                    key={isPlus ? "plus-minus" : goal}
                    className={`goal 
                        ${isPlus ? "clickable" : matchStarted ? "disabled" : "clickable"} 
                        ${(selected === goal || selected === goal + 5) ? "selected" : undefined}`}
                    onClick={isPlus ? handleTogglePlusMinus : !matchStarted ? handleClickPredictContainer : undefined}
                >
                    {selected > 4 ? (isPlus ? "-" : (goal !== 4 ? goal + 5 : "9+")) : goal}
                </div>
            );
        });
    };

    const renderResultAndPoints = (match) => {
        return (
            <>
                <div className="result">
                    <span>
                        {match.status === "finished" && match.home_goals !== null
                            ? `${match.home_goals} : ${match.away_goals} (${match.home_halftime_goals} : ${match.away_halftime_goals})`
                            : "\u00A0"
                        }
                    </span>
                </div>
                <div className="points">
                    <span>
                        {match.points.total != undefined && match.home_goals !== null 
                            ? `${match.points.total}` + " point" + (match.points.total !== 1 ? "s" : "") 
                            : "\u00A0"}
                    </span>
                </div>
            </>
        );
    }

    const renderHalftimeOptions = (matchStarted) => {
        return ["1", "X", "2"].map((option) => (
            <div
                key={option}
                className={`halftime-option 
                    ${matchStarted ? "disabled" : "clickable"}
                    ${predictions["halftime"] === option ? "selected" : undefined}`}
                onClick={!matchStarted ? handleClickPredictContainer : undefined}
            >
                {option}
            </div>
        ));
    };

    const handleTogglePlusMinus = (e) => {
        const goals = e.target.closest(".goals").querySelectorAll(".goal");
        const goalsSelected = e.target.parentElement.getAttribute("data-selected");
        const plusClicked = e.target.textContent === "+";

        if (plusClicked) {
            // Update to [5, 6, 7, 8, 9+, -]
            goals.forEach((goal, index) => {
                goal.textContent = index < 5 ? (index === 4 ? "9+" : 5 + index) : "-";
                goal.classList.remove("selected");
                if (goal.textContent === goalsSelected) {
                    goal.classList.add("selected");
                }
            });
            if (parseInt(goalsSelected) < 5) {
                goals[5].classList.add("selected");
            }
        } else {
            // Revert to [0, 1, 2, 3, 4, +]
            goals.forEach((goal, index) => {
                goal.textContent = index < 5 ? index : "+";
                goal.classList.remove("selected");
                if (goal.textContent === goalsSelected) {
                    goal.classList.add("selected");
                }
            });
            if (parseInt(goalsSelected) >= 5) {
                goals[5].classList.add("selected");
            }
        }
    };

    const handleClickPredictContainer = (e) => {
        const selected = e.target.textContent;

        if (selected === e.target.parentElement.getAttribute('data-selected')) {
            return true;
        }

        e.target.parentElement.setAttribute('data-selected', selected);

        const children = e.target.parentElement.children;
        for (const child of children) {
            child.classList.remove("selected");
            if (child.textContent === selected) {
                e.target.classList.add("selected");
            }
        }

        const match = e.target.closest('.match');
        const prediction = match.querySelectorAll('[data-selected]')
       
        let userPrediction = [match.getAttribute("data-id")];
        let updateReady = 0;
        for (const child of prediction) {
            if (child.getAttribute("data-selected") === "undefined") {
                break;
            }
            if (child.classList.contains('halftime-options')) {
                userPrediction[2] = child.getAttribute("data-selected");
            } else if (userPrediction[1] === undefined) {
                userPrediction[1] = child.getAttribute("data-selected");
            } else {
                userPrediction[3] = child.getAttribute("data-selected");
            }
            updateReady++;
        }
        if (updateReady === 3) {
            updatePrediction(userPrediction);
        }    
    };

    function updatePrediction (params) {
        if (!user) {
            setShowModal(true);
            return;
        }

        const predictions = {
            'home': params[1] === '9+' ? 9 : parseInt(params[1]),
            'halftime': params[2],
            'away': params[3] === '9+' ? 9 : parseInt(params[3])
        };
        const match_id = parseInt(params[0]);

        setPredictions(predictions);

        const submitToAPI = async () => {
            try {
                const response = await submitPrediction(match_id, predictions);
                console.log("Prediction submitted successfully:", response);
            } catch (error) {
                console.error("Failed to submit prediction:", error);
                if (error.message == 'Unauthenticated.') {
                    setShowModal(true);
                    logout(false);
                } else {
                    window.location.reload();
                }
                return;
            }
        };
        
        submitToAPI();
    }

    function determineMatchClasses()
    {
        let classes = (screen === "small" ? "match match-ss" : "match match-bs") + 
            (Object.keys(predictions).length > 0 ? " predicted" : " unpredicted");

        switch (matchStatus) {
            case "started":
                classes += " started";
                break;
            case "starting-in-one-hour":
                classes += " one-hour unfinished";
                break;
            case "starting-today":
                classes += " today unfinished";
                break;
            default:
                classes += " tomorrow unfinished";
        }

        return classes;
    }

    function renderBigScreenStructure()
    {
        return (
            <>
                <div className="team team-bs">
                    <img
                        src={match.home_image}
                        alt={`${match.home_name} logo`}
                        className="team-logo"
                    />
                    <span className="team-name">{match.home_name}</span>
                    <div 
                        className="goals" 
                        data-selected={
                            predictions["home"] === null || predictions["home"] === undefined
                                ? undefined
                                : predictions["home"] === 9 ? "9+" : predictions["home"]
                        }
                    >{renderGoals(matchStarted, "home")}</div>
                </div>

                <div className="middle">
                    <div className="middle-up">
                        <div className="match-time">
                            <span className="match-date">
                                {new Date(match.start_time * 1000).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </span>
                        </div>
                        {renderResultAndPoints(match)}
                    </div>

                    <div className="middle-down">
                        <span className="halftime-label">Halftime</span>
                        <div className="halftime-options" data-selected={`${predictions["halftime"] ?? undefined}`}>
                            {renderHalftimeOptions(matchStarted)}
                        </div>
                    </div>
                </div>

                <div className="team team-bs">
                    <img
                        src={match.away_image}
                        alt={`${match.away_name} logo`}
                        className="team-logo"
                    />
                    <span className="team-name">{match.away_name}</span>
                    <div 
                        className="goals"
                        data-selected={
                            predictions["away"] === null || predictions["away"] === undefined
                                ? undefined
                                : predictions["away"] === 9 ? "9+" : predictions["away"]
                        }
                    >{renderGoals(matchStarted, "away")}</div>
                </div>
            </>
        );
    }

    function renderSmallScreenStructure()
    {
        return (
            <>
                <div className="match-time">
                    <span className="match-date">
                        {new Date(match.start_time * 1000).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </span>
                </div>
                <div className="team team-ss">
                    <img
                        src={match.home_image}
                        alt={`${match.home_name} logo`}
                        className="team-logo"
                    />
                    <span className="team-name">{match.home_name}</span>
                    <div 
                        className="goals" 
                        data-selected={
                            predictions["home"] === null || predictions["home"] === undefined
                                ? undefined
                                : predictions["home"] === 9 ? "9+" : predictions["home"]
                        }
                    >{renderGoals(matchStarted, "home")}</div>
                </div>

                <div className="team team-ss">
                    <img
                        src={match.away_image}
                        alt={`${match.away_name} logo`}
                        className="team-logo"
                    />
                    <span className="team-name">{match.away_name}</span>
                    <div 
                        className="goals"
                        data-selected={
                            predictions["away"] === null || predictions["away"] === undefined
                                ? undefined
                                : predictions["away"] === 9 ? "9+" : predictions["away"]
                        }
                    >{renderGoals(matchStarted, "away")}</div>
                </div>

                <div className="middle-down">
                    <span className="halftime-label">Halftime</span>
                    <div className="halftime-options" data-selected={`${predictions["halftime"] ?? undefined}`}>
                        {renderHalftimeOptions(matchStarted)}
                    </div>
                </div>
                { renderResultAndPoints(match) }
            </>
        );
    }

    return (
        <div key={match.id} data-id={match.id} className={determineMatchClasses()}>
            {showModal && <AuthModal closeModal={() => setShowModal(false)} />}
            { screen === "small" ? renderSmallScreenStructure() : renderBigScreenStructure() }
        </div>
    );
}
